/**
 * Block types for the free-form page builder. Each type lists its fields; the
 * editor renders them generically and components/PageBlocks.jsx renders the page.
 *
 * Field kinds: text, textarea, select, image, icon, buttons, items (repeatable sub-fields).
 */

const ALIGN = { key: 'align', label: 'Alignment', kind: 'select', options: [['left', 'Left'], ['center', 'Centered']] };
const HEADING = { key: 'heading', label: 'Heading', kind: 'text' };
const HIGHLIGHT = { key: 'highlight', label: 'Highlighted word', kind: 'text', hint: 'Shown in your accent colour after the heading.' };
const BUTTONS = { key: 'buttons', label: 'Buttons', kind: 'buttons' };
const COLUMNS = { key: 'columns', label: 'Columns', kind: 'select', options: [['2', '2'], ['3', '3'], ['4', '4']] };

export const BLOCK_TYPES = [
  {
    type: 'text',
    label: 'Text',
    icon: 'fas fa-align-left',
    description: 'A heading with paragraphs of text.',
    fields: [HEADING, HIGHLIGHT, { key: 'body', label: 'Text', kind: 'textarea', rows: 6, hint: 'Leave a blank line between paragraphs.' }, ALIGN],
    defaults: { heading: '', highlight: '', body: '', align: 'left' },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || b.body,
  },
  {
    type: 'imageText',
    label: 'Image + text',
    icon: 'fas fa-columns',
    description: 'Picture on one side, text and buttons on the other.',
    fields: [
      { key: 'image', label: 'Image', kind: 'image' },
      { key: 'imageSide', label: 'Image side', kind: 'select', options: [['left', 'Left'], ['right', 'Right']] },
      { key: 'imageSize', label: 'Image size', kind: 'select', options: [['small', 'Small'], ['medium', 'Medium'], ['large', 'Large (half)']] },
      HEADING,
      HIGHLIGHT,
      { key: 'body', label: 'Text', kind: 'textarea', rows: 5, hint: 'Leave a blank line between paragraphs.' },
      BUTTONS,
    ],
    defaults: { image: '', imageSide: 'left', imageSize: 'medium', heading: '', highlight: '', body: '', buttons: [] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' '),
  },
  {
    type: 'image',
    label: 'Image',
    icon: 'fas fa-image',
    description: 'A single picture with an optional caption.',
    fields: [
      { key: 'url', label: 'Image', kind: 'image' },
      { key: 'size', label: 'Size', kind: 'select', options: [['small', 'Small'], ['medium', 'Medium'], ['full', 'Full width']] },
      { key: 'caption', label: 'Caption', kind: 'text' },
      { key: 'alt', label: 'Alt text (for screen readers)', kind: 'text' },
      { key: 'href', label: 'Link when clicked (optional)', kind: 'text', placeholder: '/tools or https://…' },
    ],
    defaults: { url: '', size: 'medium', caption: '', alt: '', href: '' },
    summary: (b) => b.caption,
  },
  {
    type: 'gallery',
    label: 'Gallery',
    icon: 'fas fa-images',
    description: 'A grid of pictures.',
    fields: [
      HEADING,
      HIGHLIGHT,
      COLUMNS,
      {
        key: 'images',
        label: 'Pictures',
        kind: 'items',
        itemLabel: 'Picture',
        fields: [{ key: 'url', label: 'Image', kind: 'image' }, { key: 'caption', label: 'Caption', kind: 'text' }],
        newItem: { url: '', caption: '' },
        summary: (item) => item.caption,
      },
    ],
    defaults: { heading: '', highlight: '', columns: '3', images: [] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || `${(b.images || []).length} pictures`,
  },
  {
    type: 'video',
    label: 'YouTube video',
    icon: 'fab fa-youtube',
    description: 'Embed a YouTube video.',
    fields: [
      HEADING,
      HIGHLIGHT,
      { key: 'youtube', label: 'YouTube link or ID', kind: 'text', placeholder: 'https://youtu.be/…' },
      { key: 'caption', label: 'Caption', kind: 'text' },
    ],
    defaults: { heading: '', highlight: '', youtube: '', caption: '' },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || b.youtube,
  },
  {
    type: 'cards',
    label: 'Cards',
    icon: 'fas fa-th-large',
    description: 'A grid of cards with icon or picture, title, text and link.',
    fields: [
      HEADING,
      HIGHLIGHT,
      { key: 'intro', label: 'Intro', kind: 'textarea', rows: 2 },
      COLUMNS,
      {
        key: 'items',
        label: 'Cards',
        kind: 'items',
        itemLabel: 'Card',
        fields: [
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'icon', label: 'Icon', kind: 'icon' },
          { key: 'image', label: 'Picture (replaces icon)', kind: 'image' },
          { key: 'text', label: 'Text', kind: 'textarea', rows: 3 },
          { key: 'href', label: 'Link (optional)', kind: 'text', placeholder: '/tools or https://…' },
        ],
        newItem: { title: '', icon: 'fas fa-star', image: '', text: '', href: '' },
        summary: (item) => item.title,
      },
    ],
    defaults: { heading: '', highlight: '', intro: '', columns: '3', items: [{ title: '', icon: 'fas fa-star', image: '', text: '', href: '' }] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || `${(b.items || []).length} cards`,
  },
  {
    type: 'list',
    label: 'Checklist',
    icon: 'fas fa-list-ul',
    description: 'A list of points, each with its own icon.',
    fields: [
      HEADING,
      HIGHLIGHT,
      { key: 'intro', label: 'Intro', kind: 'textarea', rows: 2 },
      {
        key: 'items',
        label: 'Points',
        kind: 'items',
        itemLabel: 'Point',
        fields: [{ key: 'text', label: 'Text', kind: 'text' }, { key: 'icon', label: 'Icon', kind: 'icon' }],
        newItem: { text: '', icon: 'fas fa-check' },
        summary: (item) => item.text,
        compact: true,
      },
    ],
    defaults: { heading: '', highlight: '', intro: '', items: [{ text: '', icon: 'fas fa-check' }] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || `${(b.items || []).length} points`,
  },
  {
    type: 'stats',
    label: 'Stats',
    icon: 'fas fa-chart-bar',
    description: 'Big numbers with a label, like "500+ streams".',
    fields: [
      HEADING,
      HIGHLIGHT,
      {
        key: 'items',
        label: 'Stats',
        kind: 'items',
        itemLabel: 'Stat',
        fields: [{ key: 'value', label: 'Number', kind: 'text', placeholder: '500+' }, { key: 'label', label: 'Label', kind: 'text', placeholder: 'Streams' }],
        newItem: { value: '', label: '' },
        summary: (item) => [item.value, item.label].filter(Boolean).join(' '),
        compact: true,
      },
    ],
    defaults: { heading: '', highlight: '', items: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || (b.items || []).map((i) => i.value).filter(Boolean).join(' · '),
  },
  {
    type: 'timeline',
    label: 'Timeline',
    icon: 'fas fa-stream',
    description: 'Dated milestones, like your streaming journey.',
    fields: [
      HEADING,
      HIGHLIGHT,
      {
        key: 'items',
        label: 'Milestones',
        kind: 'items',
        itemLabel: 'Milestone',
        fields: [
          { key: 'date', label: 'Date', kind: 'text', placeholder: '2024' },
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'text', label: 'Text', kind: 'textarea', rows: 2 },
        ],
        newItem: { date: '', title: '', text: '' },
        summary: (item) => [item.date, item.title].filter(Boolean).join(' · '),
      },
    ],
    defaults: { heading: 'My', highlight: 'Journey', items: [{ date: '', title: '', text: '' }] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' '),
  },
  {
    type: 'faq',
    label: 'FAQ',
    icon: 'fas fa-question-circle',
    description: 'Questions that open to show the answer.',
    fields: [
      HEADING,
      HIGHLIGHT,
      {
        key: 'items',
        label: 'Questions',
        kind: 'items',
        itemLabel: 'Question',
        fields: [{ key: 'question', label: 'Question', kind: 'text' }, { key: 'answer', label: 'Answer', kind: 'textarea', rows: 3 }],
        newItem: { question: '', answer: '' },
        summary: (item) => item.question,
      },
    ],
    defaults: { heading: 'FAQ', highlight: '', items: [{ question: '', answer: '' }] },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || `${(b.items || []).length} questions`,
  },
  {
    type: 'callout',
    label: 'Callout box',
    icon: 'fas fa-bullhorn',
    description: 'A highlighted box for a notice or announcement.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'text', label: 'Text', kind: 'textarea', rows: 3 },
      { key: 'icon', label: 'Icon', kind: 'icon' },
      { key: 'tone', label: 'Colour', kind: 'select', options: [['accent', 'Accent'], ['info', 'Blue'], ['success', 'Green'], ['warning', 'Amber']] },
      BUTTONS,
    ],
    defaults: { title: '', text: '', icon: 'fas fa-info-circle', tone: 'accent', buttons: [] },
    summary: (b) => b.title || b.text,
  },
  {
    type: 'quote',
    label: 'Quote',
    icon: 'fas fa-quote-left',
    description: 'A pull quote or testimonial.',
    fields: [{ key: 'text', label: 'Quote', kind: 'textarea', rows: 3 }, { key: 'cite', label: 'Who said it', kind: 'text' }],
    defaults: { text: '', cite: '' },
    summary: (b) => b.text,
  },
  {
    type: 'buttons',
    label: 'Buttons',
    icon: 'fas fa-hand-pointer',
    description: 'A row of link buttons, with an optional heading.',
    fields: [HEADING, HIGHLIGHT, BUTTONS, ALIGN],
    defaults: { heading: '', highlight: '', buttons: [{ label: '', href: '', icon: '', style: 'primary' }], align: 'left' },
    summary: (b) => [b.heading, b.highlight].filter(Boolean).join(' ') || (b.buttons || []).map((x) => x.label).filter(Boolean).join(' · '),
  },
  {
    type: 'divider',
    label: 'Divider / space',
    icon: 'fas fa-grip-lines',
    description: 'A line or some empty space between blocks.',
    fields: [{ key: 'style', label: 'Style', kind: 'select', options: [['line', 'Line'], ['space', 'Empty space']] }],
    defaults: { style: 'line' },
    summary: (b) => (b.style === 'space' ? 'Empty space' : 'Line'),
  },
];

export const ICON_SUGGESTIONS = [
  'fas fa-star', 'fas fa-check', 'fas fa-heart', 'fas fa-gamepad', 'fas fa-trophy', 'fas fa-code', 'fas fa-wrench',
  'fas fa-video', 'fas fa-microphone', 'fas fa-desktop', 'fas fa-headset', 'fas fa-bolt', 'fas fa-rocket',
  'fas fa-users', 'fas fa-comments', 'fas fa-calendar', 'fas fa-clock', 'fas fa-info-circle', 'fas fa-gift',
  'fas fa-mug-hot', 'fas fa-music', 'fas fa-camera', 'fas fa-palette', 'fas fa-globe', 'fas fa-envelope',
  'fab fa-twitch', 'fab fa-youtube', 'fab fa-discord', 'fab fa-x-twitter', 'fab fa-tiktok', 'fab fa-instagram',
  'fab fa-steam', 'fab fa-playstation', 'fab fa-xbox', 'fab fa-github',
];

export function getBlockType(type) {
  return BLOCK_TYPES.find((t) => t.type === type) || { type, label: type, icon: 'fas fa-cube', fields: [], defaults: {}, summary: () => '' };
}

export function newBlockId() {
  return `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function createBlock(type) {
  const meta = getBlockType(type);
  return { id: newBlockId(), type, ...JSON.parse(JSON.stringify(meta.defaults)) };
}
